import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { logout, setTokens, selectLoginAt } from './authSlice';
import { useRefreshMutation } from '../../api/authApi';

// Floor required by the clinic: a session must stay usable for AT LEAST 1 hour in any scenario
// (idle or active). These are kept generously above that floor with margin, and kept in sync
// with the backend's Jwt:AbsoluteSessionHours (appsettings.json) — the backend is the real
// authority (a refresh past its ceiling fails server-side regardless of what the client thinks),
// this is just how long the client waits before giving up on its own.
const INACTIVITY_MS = 4 * 60 * 60 * 1000;  // 4 hours of no mouse/keyboard/scroll/touch → logout
const ABSOLUTE_MS   = 8 * 60 * 60 * 1000;  // 8-hour hard ceiling from initial login (matches backend)
// Refresh the access token this long before it actually expires, so normal use never has to rely
// on a reactive 401 → refresh round-trip (which is also now race-safe, but proactive is better:
// the user never sees a momentary failed request while it's in flight).
const PROACTIVE_REFRESH_LEAD_MS = 5 * 60 * 1000;

/**
 * Watches the auth state to enforce session lifetime rules:
 *   1) Proactive token refresh shortly before the access token expires (keeps active users
 *      logged in indefinitely, up to the absolute ceiling, without ever hitting a 401).
 *   2) Inactivity expiry: logout after a long stretch of no activity at all.
 *   3) Absolute session ceiling: hard stop from initial login regardless of activity.
 *
 * Also redirects to /login + shows a toast whenever auth → unauth transition is detected.
 *
 * Mount once near the App root, INSIDE the Router and the Redux Provider.
 */
export default function SessionGuard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const accessToken = useSelector((s) => s.auth?.accessToken);
  const refreshToken = useSelector((s) => s.auth?.refreshToken);
  const expiresAt   = useSelector((s) => s.auth?.accessTokenExpiresAt);
  const loginAt     = useSelector(selectLoginAt);
  const wasAuthRef  = useRef(Boolean(accessToken));
  const [refresh]   = useRefreshMutation();

  // ── 1) Proactive refresh before the access token expires ─────────────────
  useEffect(() => {
    if (!accessToken || !expiresAt || !refreshToken) return undefined;
    const msUntilExpiry = new Date(expiresAt).getTime() - Date.now();
    const msUntilRefresh = Math.max(0, msUntilExpiry - PROACTIVE_REFRESH_LEAD_MS);
    const id = setTimeout(async () => {
      try {
        const data = await refresh({ refreshToken }).unwrap();
        dispatch(setTokens({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          accessTokenExpiresAt: data.accessTokenExpiresAt,
          refreshTokenExpiresAt: data.refreshTokenExpiresAt,
          user: data.user,
        }));
      } catch {
        // Genuinely expired/revoked (e.g. past the absolute ceiling) — let the access-token
        // clock run out naturally; the API layer's reactive 401 handling will log out cleanly.
      }
    }, msUntilRefresh);
    return () => clearTimeout(id);
  }, [accessToken, expiresAt, refreshToken, refresh, dispatch]);

  // ── 2) Inactivity expiry ──────────────────────────────────────────────────
  useEffect(() => {
    if (!accessToken) return undefined;
    let id = setTimeout(() => dispatch(logout()), INACTIVITY_MS);
    const reset = () => {
      clearTimeout(id);
      id = setTimeout(() => dispatch(logout()), INACTIVITY_MS);
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((ev) => window.addEventListener(ev, reset, { passive: true }));
    return () => {
      clearTimeout(id);
      events.forEach((ev) => window.removeEventListener(ev, reset));
    };
  }, [accessToken, dispatch]);

  // ── 3) Absolute session ceiling (from initial login) ─────────────────────
  useEffect(() => {
    if (!accessToken || !loginAt) return undefined;
    const remaining = loginAt + ABSOLUTE_MS - Date.now();
    if (remaining <= 0) { dispatch(logout()); return undefined; }
    const id = setTimeout(() => dispatch(logout()), remaining);
    return () => clearTimeout(id);
  }, [accessToken, loginAt, dispatch]);

  // ── 4) Redirect + toast on auth → unauth transition ──────────────────────
  useEffect(() => {
    const isAuth = Boolean(accessToken);
    if (wasAuthRef.current && !isAuth) {
      toast.info('Session expired. Please log in again.', { toastId: 'session-expired' });
      if (location.pathname !== '/login') {
        navigate('/login', { replace: true, state: { from: location.pathname } });
      }
    }
    wasAuthRef.current = isAuth;
  }, [accessToken, navigate, location.pathname]);

  return null;
}
